import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { has, isEqual, omit } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import {
  cloneDeep,
  compact,
  concat,
  filter,
  find,
  findIndex,
  first,
  flatMap,
  forEach,
  get,
  includes,
  isEmpty,
  isNil,
  map,
  reduce,
  slice,
  some,
} from 'lodash';
import { Row } from 'read-excel-file';

import type { ManifestCaseTarget } from './dialogState';
import {
  addCaseToRelevantList,
  getCaseFromRowCombinedWithRealData,
  getSlideIdentifierMismatchWarning,
  ManifestError,
  readExplicitNewCaseIntent,
  validateMandatoryHeaders,
} from './helpers';

export const unmatchedSlideIdentityMessage = (label: string) =>
  `${label} was not matched to a lost and found slide in this lab or study. Select the slide from Lab lost and found slides or Study lost and found slides.`;

const getManifestRowMatchError = ({
  rowNumber,
  slideId,
  fileName,
}: {
  rowNumber: number;
  slideId?: string;
  fileName?: string;
}) => {
  if (fileName) {
    return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage(`File name ${fileName}`)}`;
  }

  if (slideId) {
    return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage(`Slide ID ${slideId}`)}`;
  }

  return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage('Slide identifier')}`;
};

export interface ManifestWorkflowResolution {
  errorMessages: string[];
  warningMessages: string[];
  onlyManifestCases: Procedure[];
  resolvedCases: Procedure[];
  notIngestedSlides: Procedure[];
}

export interface UploadedManifestResolution {
  errorMessages: string[];
  warningMessages: string[];
  manifestCases: Procedure[];
  notIngestedSlides: Procedure[];
  slideIdsToFetch: string[];
  explicitNewCaseSlideIds: string[];
}

interface ResolveUploadedManifestRowsInput {
  editableExistingAndMockCases: Procedure[];
  notEditableSlidesMockCases: Procedure[];
  excelRows: Row[];
  procedureFieldContext: ProceduresFieldsContext;
  labId: string;
  studyId: string;
  preserveInvalidEditableValues?: boolean;
}

const EDITABLE_CATALOG_FIELD_ERROR =
  /^(?:Error in row \d+: )?Invalid (?:Cancer Type|Biopsy Site|Stain Type|Biopsy Type|Preservation Method|Procedure Type)\. Choose a current option and upload again\.$/;

export const canOfferFixInCasePlan = (messages: string[]) =>
  messages.length > 0 &&
  messages.every((message) => EDITABLE_CATALOG_FIELD_ERROR.test(message) || /, On page \d+$/.test(message));

interface ResolveManifestWorkflowInput {
  existingProcedures: Procedure[];
  manualManifestCases: Procedure[];
  uploadedManifestCases: Procedure[];
  uploadedNotIngestedSlides: Procedure[];
  uploadedWarningMessages: string[];
  uploadedErrorMessages: string[];
  overrideCases: boolean;
}

export const getManifestCaseTargets = ({
  cases,
  existingProcedures,
  assignmentsBySlideId,
}: {
  cases: Procedure[];
  existingProcedures: Procedure[];
  assignmentsBySlideId: Record<string, ManifestCaseTarget>;
}): Record<string, ManifestCaseTarget> => {
  const targets = { ...assignmentsBySlideId };
  forEach(cases, (procedure) => {
    const isSavedCase =
      !get(procedure, 'isPendingSlideMockCase') &&
      !get(procedure, 'isMockCaseOfPendingSlide') &&
      (some(existingProcedures, { id: procedure.id }) || (procedure.id > 0 && Boolean(procedure.studyId)));
    forEach(procedure.slides, (slide) => {
      if (!has(targets, [slide.id])) {
        targets[slide.id] = isSavedCase ? procedure.id : 'new';
      }
    });
  });
  return targets;
};

export const resolveManifestExistingCaseAssignments = ({
  cases,
  existingProcedures,
  assignmentsBySlideId,
  casePatchesById,
  labId,
  studyId,
}: {
  cases: Procedure[];
  existingProcedures: Procedure[];
  assignmentsBySlideId: Record<string, ManifestCaseTarget>;
  casePatchesById: Record<number, Partial<CaseUpdate>>;
  labId: string;
  studyId: string;
}): { cases: Procedure[]; missingTargetCount: number } => {
  const resolvedCases: Procedure[] = [];
  const targetCasesById = new Map<number, Procedure>();
  let missingTargetCount = 0;

  forEach(cases, (procedure) => {
    const retainedSlides = filter(
      procedure.slides,
      (slide) => !has(assignmentsBySlideId, [slide.id]) || assignmentsBySlideId[slide.id] === 'new'
    );
    if (!isEmpty(retainedSlides)) {
      const retainedCase = { ...procedure, slides: map(retainedSlides, (slide) => ({ ...slide })) };
      resolvedCases.push(retainedCase);
      if (some(existingProcedures, { id: procedure.id })) targetCasesById.set(procedure.id, retainedCase);
    }
  });

  forEach(cases, (procedure) => {
    const unresolvedSlides: Slide[] = [];
    forEach(procedure.slides, (slide) => {
      if (!has(assignmentsBySlideId, [slide.id])) return;
      const targetCaseId = assignmentsBySlideId[slide.id];
      if (targetCaseId === 'new') return;
      const existingProcedure = find(
        existingProcedures,
        (candidate) =>
          candidate.id === targetCaseId &&
          (!candidate.labId || candidate.labId === labId) &&
          (!candidate.studyId || candidate.studyId === studyId)
      );
      if (!existingProcedure) {
        missingTargetCount += 1;
        unresolvedSlides.push({ ...slide });
        return;
      }

      const resolvedCase = targetCasesById.get(existingProcedure.id);
      if (resolvedCase) {
        if (!some(resolvedCase.slides, { id: slide.id })) resolvedCase.slides?.push({ ...slide });
      } else {
        const assignedCase = {
          ...existingProcedure,
          ...omit(casePatchesById[existingProcedure.id], ['label']),
          slides: [{ ...slide }],
        };
        targetCasesById.set(existingProcedure.id, assignedCase);
        resolvedCases.push(assignedCase);
      }
    });
    if (!isEmpty(unresolvedSlides)) resolvedCases.push({ ...procedure, slides: unresolvedSlides });
  });

  return { cases: resolvedCases, missingTargetCount };
};

export const collectManifestCaseLabelErrors = (cases: Procedure[], existingProcedures: Procedure[]) => {
  const existingCaseIds = new Set(map(existingProcedures, 'id'));
  const existingIdsByLabel = new Map<string, Set<number>>();
  const draftIdsByLabel = new Map<string, Set<number>>();

  forEach(existingProcedures, (procedure) => {
    if (!procedure.label) return;
    const caseIds = existingIdsByLabel.get(procedure.label) || new Set<number>();
    caseIds.add(procedure.id);
    existingIdsByLabel.set(procedure.label, caseIds);
  });

  let hasConflict = false;
  forEach(cases, (procedure) => {
    if (!procedure.label) return;
    const existingIds = existingIdsByLabel.get(procedure.label);
    const isExistingCase = !get(procedure, 'isMockCaseOfPendingSlide') && existingCaseIds.has(procedure.id);
    if ((existingIds && existingIds.size > 1) || (isExistingCase && existingIds && !existingIds.has(procedure.id))) {
      hasConflict = true;
    }
    if (isExistingCase) {
      const draftIds = draftIdsByLabel.get(procedure.label) || new Set<number>();
      draftIds.add(procedure.id);
      draftIdsByLabel.set(procedure.label, draftIds);
      if (draftIds.size > 1) hasConflict = true;
    }
  });

  return hasConflict ? ['Case labels conflict with another case in this study. Choose a unique label.'] : [];
};

export const collectExplicitNewCaseLabelErrors = (
  cases: Procedure[],
  existingProcedures: Procedure[],
  targetsBySlideId: Readonly<Record<string, ManifestCaseTarget | undefined>>
) => {
  const savedLabels = new Set(compact(map(existingProcedures, 'label')));
  const hasExplicitNewCaseCollision = some(
    cases,
    (procedure) =>
      Boolean(procedure.label) &&
      savedLabels.has(procedure.label) &&
      some(procedure.slides, (slide) => targetsBySlideId[slide.id] === 'new')
  );
  return hasExplicitNewCaseCollision
    ? ['Case labels conflict with another case in this study. Choose a unique label.']
    : [];
};

const CASE_SHARED_METADATA_ERROR_LIMIT = 10;

const sharedMetadataValue = (value: unknown, emptyValue: unknown = null) => (value == null ? emptyValue : value);

export const collectCaseSharedMetadataErrors = (cases: Procedure[]) => {
  const firstValuesByLabel = new Map<
    string,
    { cancerTypeId: unknown; cancerSubtypes: unknown; biopsySiteId: unknown; biopsyType: unknown }
  >();
  const reportedConflicts = new Set<string>();
  const errors: string[] = [];
  let omittedConflictCount = 0;

  const reportConflict = (label: string, field: string) => {
    const conflictKey = `${label}\0${field}`;
    if (reportedConflicts.has(conflictKey)) return;
    reportedConflicts.add(conflictKey);
    if (errors.length < CASE_SHARED_METADATA_ERROR_LIMIT) {
      errors.push(
        `${field} conflicts across slides in case ${label}. Use the same value for every slide in this case.`
      );
      return;
    }
    omittedConflictCount += 1;
  };

  forEach(cases, (procedure) => {
    if (!procedure.label) return;
    forEach(procedure.slides, (slide) => {
      const currentValues = {
        cancerTypeId: sharedMetadataValue(procedure.cancerTypeId),
        cancerSubtypes: sharedMetadataValue(procedure.cancerSubtypes, []),
        biopsySiteId: sharedMetadataValue(slide.biopsySiteId),
        biopsyType: sharedMetadataValue(slide.biopsyType),
      };
      const firstValues = firstValuesByLabel.get(procedure.label);
      if (!firstValues) {
        firstValuesByLabel.set(procedure.label, currentValues);
        return;
      }
      if (!isEqual(currentValues.cancerTypeId, firstValues.cancerTypeId))
        reportConflict(procedure.label, 'Cancer Type');
      if (!isEqual(currentValues.cancerSubtypes, firstValues.cancerSubtypes)) {
        reportConflict(procedure.label, 'Cancer Subtype');
      }
      if (!isEqual(currentValues.biopsySiteId, firstValues.biopsySiteId))
        reportConflict(procedure.label, 'Biopsy Site');
      if (!isEqual(currentValues.biopsyType, firstValues.biopsyType)) reportConflict(procedure.label, 'Biopsy Type');
    });
  });

  if (omittedConflictCount > 0) {
    errors.push(
      omittedConflictCount === 1
        ? '1 more case metadata conflict needs the same value on every slide in that case.'
        : `${omittedConflictCount} more case metadata conflicts need the same value on every slide in that case.`
    );
  }

  return errors;
};

export const mergeManifestCases = (baseCases: Procedure[], casesToAdd: Procedure[]) => {
  return reduce(
    casesToAdd,
    (cases, caseToAdd) => {
      const slideToAdd = first(caseToAdd.slides);
      if (!slideToAdd) {
        return cases;
      }

      const existingCaseIndex = findIndex(cases, { label: caseToAdd.label });

      if (existingCaseIndex === -1) {
        return [...cases, cloneDeep(caseToAdd)];
      }

      const existingCase = cloneDeep(cases[existingCaseIndex]);
      const slidesToAdd = filter(
        caseToAdd.slides,
        (incomingSlide) => !some(existingCase.slides, (existingSlide) => existingSlide.id === incomingSlide.id)
      );

      if (isEmpty(slidesToAdd)) {
        return cases;
      }

      existingCase.slides = [...(existingCase.slides || []), ...cloneDeep(slidesToAdd)];

      return [...slice(cases, 0, existingCaseIndex), existingCase, ...slice(cases, existingCaseIndex + 1)];
    },
    cloneDeep(baseCases)
  );
};

export const combineManifestCasesWithExistingCases = (
  manifestCases: Procedure[],
  existingProcedures: Procedure[],
  isExcludedFromExistingCaseMatch: (manifestCase: Procedure) => boolean = () => false
): { cases: Procedure[]; warningMessages: string[] } => {
  if (isEmpty(manifestCases)) {
    return { cases: [], warningMessages: [] };
  }

  const warningMessages: string[] = [];
  const combinedCases = cloneDeep(manifestCases);
  const manifestSlideIds = map(flatMap(combinedCases, 'slides'), 'id');

  forEach(combinedCases, (manifestCase) => {
    const existingCase = isExcludedFromExistingCaseMatch(manifestCase)
      ? undefined
      : find(existingProcedures, { label: manifestCase.label });

    if (!existingCase) {
      return;
    }

    const slidesToAdd = filter(
      existingCase.slides || [],
      (existingSlide) => !includes(map(manifestCase.slides, 'id'), existingSlide.id)
    );

    forEach(slidesToAdd, (slideToAdd) => {
      if (includes(manifestSlideIds, slideToAdd.id)) {
        warningMessages.push(
          `Slide id ${slideToAdd.id} found also in Case ${manifestCase.label}. Moving the slide from the old case to the new case.`
        );
      }
    });

    manifestCase.slides = concat(
      manifestCase.slides || [],
      filter(slidesToAdd, (slide) => !includes(manifestSlideIds, slide.id))
    );
  });

  // A draft case excluded from matching keeps its saved namesake visible with the slides that stay in it.
  const retainedSavedCases: Procedure[] = [];
  forEach(combinedCases, (manifestCase) => {
    if (!isExcludedFromExistingCaseMatch(manifestCase)) return;
    const savedCase = find(existingProcedures, { label: manifestCase.label });
    const isRepresented =
      !savedCase ||
      some(
        combinedCases,
        (candidate) => !isExcludedFromExistingCaseMatch(candidate) && candidate.id === savedCase.id
      ) ||
      some(retainedSavedCases, { id: savedCase.id });
    const remainingSlides = filter(savedCase?.slides, (slide) => !includes(manifestSlideIds, slide.id));
    if (isRepresented || isEmpty(remainingSlides)) return;
    retainedSavedCases.push({ ...cloneDeep(savedCase), slides: remainingSlides });
  });

  return { cases: concat(combinedCases, retainedSavedCases), warningMessages };
};

export const applyManifestDraftPatches = (
  baseCases: Procedure[],
  casePatchesById: Record<number, Partial<CaseUpdate>>,
  slidePatchesById: Record<string, Partial<SlideUpdate>>
) => {
  return map(baseCases, (baseCase) => ({
    ...baseCase,
    ...(casePatchesById[baseCase.id] || {}),
    slides: map(baseCase.slides, (slide) => ({
      ...slide,
      ...(slidePatchesById[slide.id] || {}),
    })),
  }));
};

export const removeSlidesFromManifestCases = (baseCases: Procedure[], removedSlideIds: string[]) => {
  if (isEmpty(removedSlideIds)) {
    return cloneDeep(baseCases);
  }

  return compact(
    map(baseCases, (baseCase) => {
      const slides = filter(baseCase.slides, (slide) => !includes(removedSlideIds, slide.id));

      if (isEmpty(slides)) {
        return null;
      }

      return {
        ...cloneDeep(baseCase),
        slides,
      };
    })
  );
};

export const resolveUploadedManifestRows = ({
  editableExistingAndMockCases,
  notEditableSlidesMockCases,
  excelRows,
  procedureFieldContext,
  labId,
  studyId,
  preserveInvalidEditableValues = false,
}: ResolveUploadedManifestRowsInput): UploadedManifestResolution => {
  const warningMessages: string[] = [];
  const duplicateRowErrors: string[] = [];
  let duplicateRowCount = 0;
  let manifestCases: Procedure[] = [];
  let notIngestedSlides: Procedure[] = [];
  let slideIdsToFetch: string[] = [];
  let explicitNewCaseSlideIds: string[] = [];
  let errorMessages: string[] = [];

  if (isEmpty(excelRows) || excelRows.length === 1) {
    errorMessages = ['No data found in the file'];
  } else if (!isEmpty(excelRows)) {
    const fileHeaders = map(first(excelRows), (header) => String(header).toLowerCase());

    try {
      validateMandatoryHeaders(fileHeaders);

      const allCases = concat(editableExistingAndMockCases, notEditableSlidesMockCases);
      const firstRowBySlideId = new Map<string, number>();
      const firstRowByCaseLabel = new Map<string, { procedure: Procedure; rowNumber: number }>();

      forEach(slice(excelRows, 1), (row: Row, index) => {
        let newCase: Procedure;
        const rowNumber = index + 1;

        let explicitNewCase = false;
        try {
          explicitNewCase = readExplicitNewCaseIntent(fileHeaders, row);
          newCase = getCaseFromRowCombinedWithRealData(
            procedureFieldContext,
            fileHeaders,
            row,
            labId,
            studyId,
            index,
            editableExistingAndMockCases,
            notEditableSlidesMockCases,
            preserveInvalidEditableValues
          );
        } catch (error) {
          if (error instanceof ManifestError) {
            throw new ManifestError(`Error in row ${rowNumber}: ${error.message}`);
          }

          throw error;
        }

        const currentSlide = first(newCase.slides);
        if (includes(fileHeaders, 'slide id') && isNil(currentSlide?.originalFileName) && currentSlide?.id) {
          slideIdsToFetch.push(currentSlide.id);
          return;
        }

        const identifierMismatchWarning = getSlideIdentifierMismatchWarning(fileHeaders, row, currentSlide, rowNumber);
        if (identifierMismatchWarning) warningMessages.push(identifierMismatchWarning);

        if (currentSlide?.id) {
          const firstRow = firstRowBySlideId.get(currentSlide.id);
          if (firstRow !== undefined) {
            duplicateRowCount += 1;
            if (duplicateRowErrors.length < 10) {
              duplicateRowErrors.push(
                `Error in row ${rowNumber}: Slide ID ${currentSlide.id} also appears in row ${firstRow}. Keep one row per slide and upload again.`
              );
            }
            return;
          }
          firstRowBySlideId.set(currentSlide.id, rowNumber);
          if (explicitNewCase) explicitNewCaseSlideIds.push(currentSlide.id);
        }

        if (newCase.label) {
          const firstCaseRow = firstRowByCaseLabel.get(newCase.label);
          if (firstCaseRow) {
            const firstSlide = first(firstCaseRow.procedure.slides);
            const sharedFields = [
              ['Cancer Type', newCase.cancerTypeId ?? null, firstCaseRow.procedure.cancerTypeId ?? null],
              ['Cancer Subtype', newCase.cancerSubtypes ?? [], firstCaseRow.procedure.cancerSubtypes ?? []],
              ['Biopsy Site', currentSlide?.biopsySiteId ?? null, firstSlide?.biopsySiteId ?? null],
              ['Biopsy Type', currentSlide?.biopsyType ?? null, firstSlide?.biopsyType ?? null],
            ];
            const conflict = find(sharedFields, ([, incomingValue, firstValue]) => !isEqual(incomingValue, firstValue));
            if (conflict) {
              throw new ManifestError(
                `Error in row ${rowNumber}: ${conflict[0]} conflicts with row ${firstCaseRow.rowNumber} for the same case label. Use the same value across this case's slides and upload again.`
              );
            }
          } else {
            firstRowByCaseLabel.set(newCase.label, { procedure: newCase, rowNumber });
          }
        }

        const previousMissingSlideCount = flatMap(notIngestedSlides, 'slides').length;
        const nextResult = addCaseToRelevantList(newCase, notIngestedSlides, manifestCases, allCases);
        manifestCases = nextResult.updatedCases;
        notIngestedSlides = nextResult.updatedMissingSlides;

        if (flatMap(nextResult.updatedMissingSlides, 'slides').length > previousMissingSlideCount) {
          errorMessages.push(
            getManifestRowMatchError({ rowNumber, slideId: currentSlide?.id, fileName: currentSlide?.originalFileName })
          );
        }

        if (nextResult.warning) {
          warningMessages.push(nextResult.warning);
        }
      });
    } catch (error) {
      if (error instanceof ManifestError) {
        errorMessages = [error.message];
        manifestCases = [];
        notIngestedSlides = [];
        slideIdsToFetch = [];
        explicitNewCaseSlideIds = [];
      } else {
        throw error;
      }
    }
  }

  if (duplicateRowCount > 0) {
    return {
      errorMessages: concat(
        duplicateRowErrors,
        duplicateRowCount > duplicateRowErrors.length
          ? [
              `${
                duplicateRowCount - duplicateRowErrors.length
              } additional duplicate slide rows. Keep one row per slide and upload again.`,
            ]
          : []
      ),
      warningMessages: [],
      manifestCases: [],
      notIngestedSlides: [],
      slideIdsToFetch: [],
      explicitNewCaseSlideIds: [],
    };
  }

  return {
    errorMessages,
    warningMessages,
    manifestCases,
    notIngestedSlides,
    slideIdsToFetch: errorMessages.length > 0 ? [] : slideIdsToFetch,
    explicitNewCaseSlideIds: errorMessages.length > 0 ? [] : explicitNewCaseSlideIds,
  };
};

export const resolveManifestWorkflow = ({
  existingProcedures,
  manualManifestCases,
  uploadedManifestCases,
  uploadedNotIngestedSlides,
  uploadedWarningMessages,
  uploadedErrorMessages,
  overrideCases,
}: ResolveManifestWorkflowInput): ManifestWorkflowResolution => {
  const unmergedCases = concat(uploadedManifestCases, manualManifestCases);
  const hasLabelConflicts = !isEmpty(collectManifestCaseLabelErrors(unmergedCases, existingProcedures));
  const onlyManifestCases = hasLabelConflicts
    ? cloneDeep(unmergedCases)
    : mergeManifestCases(uploadedManifestCases, manualManifestCases);

  const combinedCases =
    overrideCases || hasLabelConflicts
      ? { cases: cloneDeep(onlyManifestCases), warningMessages: [] }
      : combineManifestCasesWithExistingCases(onlyManifestCases, existingProcedures);

  return {
    errorMessages: uploadedErrorMessages,
    warningMessages: [...uploadedWarningMessages, ...combinedCases.warningMessages],
    onlyManifestCases,
    resolvedCases: combinedCases.cases,
    notIngestedSlides: uploadedNotIngestedSlides,
  };
};

export const filterCasesWithErrors = ({
  cases,
  fields,
  procedureFieldContext,
}: {
  cases: Procedure[];
  fields: any[];
  procedureFieldContext: ProceduresFieldsContext;
}) => {
  const caseIdsWithError = new Set<number>();
  const slideIdsWithError = new Set<string>();

  forEach(cases, (procedure) => {
    forEach(procedure.slides, (slide) => {
      const row = {
        ...procedure,
        ...slide,
        slides: [slide],
        _unwoundRowMetadata: {
          baseRowId: procedure.id,
          innerRowId: slide.id,
        },
      };

      const hasError = some(fields, (field) =>
        field?.getError?.({ value: get(row, field.dataKey), row, context: procedureFieldContext })
      );

      if (hasError) {
        caseIdsWithError.add(procedure.id);
        slideIdsWithError.add(slide.id);
      }
    });
  });

  return map(
    filter(cases, (procedure) => caseIdsWithError.has(procedure.id)),
    (procedure) => ({
      ...procedure,
      slides: filter(procedure.slides, (slide) => slideIdsWithError.has(slide.id)),
    })
  );
};

export const collectManifestValidationErrors = ({
  cases,
  fields,
  procedureFieldContext,
  rowsPerPage,
}: {
  cases: Procedure[];
  fields: any[];
  procedureFieldContext: ProceduresFieldsContext;
  rowsPerPage: number;
}) => {
  const validationErrors: string[] = [];
  let rowIndex = 0;

  forEach(cases, (procedure) => {
    forEach(procedure.slides, (slide) => {
      const row = {
        ...procedure,
        ...slide,
        slides: [slide],
        _unwoundRowMetadata: {
          baseRowId: procedure.id,
          innerRowId: slide.id,
        },
      };

      forEach(fields, (field) => {
        const cellError = field?.getError?.({ value: get(row, field.dataKey), row, context: procedureFieldContext });

        if (cellError) {
          validationErrors.push(`${cellError}, On page ${Math.floor(rowIndex / rowsPerPage + 1)}`);
        }
      });

      rowIndex += 1;
    });
  });

  return Array.from(new Set(validationErrors));
};
